import { useSwrGlobal } from '@hooks/useSwrApi';
import { ApiSavedResumeSearch } from '@type/v1-api-types';
import { BellAlertIcon } from '@heroicons/react/20/solid';
import { Button } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrSaveResumeSearchAlert } from '@hooks/useSwrApi/endpoints/resume-search-alert';


// Interface
// **********************************************
interface SaveSearchAlertProps {
  locale?: string,
  savedResumeSearch?: ApiSavedResumeSearch,
  revalidateLastPage?: () => void,
}


/**
 * @description Renders the save search alert button.
 * - **locale** The locale of the app
 * - **savedResumeSearch** The saved resume search object
 * - **revalidateLastPage** The revalidate function for the last page
 */
export const SaveSearchAlert = (props: SaveSearchAlertProps) => {
  // Destructure props
  const {
    locale = 'ro',
    savedResumeSearch,
    revalidateLastPage,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // We're going to use the global SWR to update the resume search alerts
  const { revalidateKey } = useSwrGlobal();


  // Callback to execute on save success. Revalidate the last page and the alerts list.
  const successCallback = () => {
    if (revalidateLastPage) revalidateLastPage();
    revalidateKey('/v2/resume-search-alerts');
  };


  // SWR: save resume search alert
  const availableId = savedResumeSearch?.resumeSearchAlertId || savedResumeSearch?.resumeSearchId || '';
  const { trigger, isMutating } = useSwrSaveResumeSearchAlert(locale, availableId, successCallback);

  // Save alert action. Refresh the infinite list if provided, so we can see the new alert in the list.
  const saveAlert = () => {
    if (availableId) void trigger({});
  };

  // Early return
  const nothingToSave = !savedResumeSearch
    || savedResumeSearch?.isAlertActive
    || (!savedResumeSearch?.isAlertActive && !availableId);
  if (nothingToSave) return null;

  // Render component
  // **********************************************
  return (
    <Button isLoading={isMutating} spinnerClass="-ml-1 mr-3 h-4 w-4" styling="text" color="pink" size="sm" className="!text-left !font-medium" onClick={saveAlert}>
      {!isMutating && <BellAlertIcon className="mr-2 size-4 shrink-0" />}
      {t('search.save.notification.alert')}
    </Button>
  );
};
